/**
 * GPTZero: AI Detection & Writing Replay
 *
 * Exposes a single all-origins WAR group with no `use_dynamic_url` globbing the PNGs under
 * assets (including the nested keystroke-detector images). The globs resolve to stable
 * vendor-named PNGs, so each is fetchable from any page at a fixed URL. The remaining WAR
 * are either origin-scoped or build-hashed.
 *
 * The images are the cleanest signal: they load through `probeImage` with no CORS grant,
 * so they survive a strict page CSP.
 *
 * Manifest verified against version 2026.7.2.
 */

import { probeImage } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'kgobeoibakoahbfnlficpmibdbkdchap';

export const gptzero: ExtensionDefinition = {
  id: ID,
  name: 'GPTZero: AI Detection & Writing Replay',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'PNG assets globbed to every origin with no dynamic URL. The icon load is conclusive ' +
    'and CORS-free; a keystroke-detector image corroborates.',
  signals: [
    {
      id: 'war-icon-image',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icon.png via <img> — a static icon exposed to every origin; needs ' +
        'no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icon.png'),
    },
    {
      id: 'war-keystroke-natural-0',
      kind: 'web-accessible-resource',
      description:
        'Load assets/keystrokeDetector/natural_0.png via <img> — a vendor-named path ' +
        'unique to GPTZero, corroborating the icon hit.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/keystrokeDetector/natural_0.png'),
    },
    {
      id: 'war-keystroke-natural-10',
      kind: 'web-accessible-resource',
      description:
        'Load assets/keystrokeDetector/natural_10.png via <img> — a second image in the ' +
        'same set, an independent path.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/keystrokeDetector/natural_10.png'),
    },
  ],
};
